/** Alta y edición de un asiento manual: fecha, concepto y líneas de debe y haber, con el cuadre a la vista. */
import { useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, CheckCircle2, Loader2, Plus, Trash2, X } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import { todayYmd } from '../../lib/ctb'
import type { CtbAccountDto, CtbEntryDto } from '@toga/shared'
import AccountInput, { AccountDatalist } from './AccountInput'

const inputCls =
  'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

interface Row {
  code: string
  debit: string
  credit: string
  concept: string
}

const emptyRow = (): Row => ({ code: '', debit: '', credit: '', concept: '' })

interface Props {
  issuerId: string
  accounts: CtbAccountDto[]
  entry: CtbEntryDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}

export function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const b = err.body as { error?: string; errors?: { message: string }[] } | undefined
    if (b?.errors && b.errors.length > 1) return b.errors.map((e) => e.message).join(' · ')
    return b?.error ?? fallback
  }
  return err instanceof Error ? err.message : fallback
}

export default function EntryModal({ issuerId, accounts, entry, onClose, onSaved }: Props) {
  const editing = entry !== null
  const [date, setDate] = useState(entry?.date ?? todayYmd())
  const [concept, setConcept] = useState(entry?.concept ?? '')
  const [rows, setRows] = useState<Row[]>(
    entry
      ? entry.lines.map((l) => ({
          code: l.accountCode,
          debit: centsToEurosInput(l.debitCents),
          credit: centsToEurosInput(l.creditCents),
          concept: l.concept ?? '',
        }))
      : [emptyRow(), emptyRow()]
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const totals = useMemo(() => {
    const debit = rows.reduce((a, r) => a + parseEurosToCents(r.debit), 0)
    const credit = rows.reduce((a, r) => a + parseEurosToCents(r.credit), 0)
    return { debit, credit, diff: debit - credit }
  }, [rows])

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  const payloadLines = () =>
    rows
      .filter((r) => r.code || r.debit || r.credit)
      .map((r) => ({
        accountCode: r.code,
        debitCents: parseEurosToCents(r.debit),
        creditCents: parseEurosToCents(r.credit),
        concept: r.concept.trim() || null,
      }))

  const save = async (post: boolean) => {
    setError(null)
    if (!concept.trim()) return setError('Indica el concepto')
    const lines = payloadLines()
    if (lines.some((l) => l.debitCents > 0 && l.creditCents > 0)) {
      return setError('Una línea lleva el importe en el debe o en el haber, no en los dos')
    }
    setBusy(true)
    try {
      if (editing) {
        await api.patch(`/ctb/entries/${entry.id}`, {
          issuerId,
          date,
          concept: concept.trim(),
          lines,
          version: entry.version,
        })
        if (post) await api.post(`/ctb/entries/${entry.id}/post`, { issuerId })
      } else {
        await api.post('/ctb/entries', { issuerId, date, concept: concept.trim(), lines, post })
      }
      await onSaved()
    } catch (err) {
      setError(apiMessage(err, 'No se pudo guardar'))
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void save(false)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto py-8"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl surface-deep p-5 animate-slideUp space-y-4"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold t-primary">
              {editing ? 'Editar asiento (borrador)' : 'Nuevo asiento manual'}
            </h3>
            <p className="text-xs t-muted mt-0.5">
              Un asiento cuadra cuando el debe y el haber suman lo mismo. Un borrador puede estar
              incompleto; para contabilizar tiene que cuadrar y tener al menos dos líneas.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="t-muted hover:t-primary"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-3">
          <label className="block">
            <span className="text-xs t-muted">Fecha</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={`${inputCls} mt-0.5`}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Concepto</span>
            <input
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              maxLength={300}
              className={`${inputCls} mt-0.5`}
              required
            />
          </label>
        </div>

        <AccountDatalist id="ctb-accounts-list" accounts={accounts} />
        <div className="space-y-1.5">
          <div className="hidden sm:grid grid-cols-[9rem_1fr_8rem_8rem_2rem] gap-2 text-[11px] uppercase tracking-wider t-faint px-1">
            <span>Cuenta</span>
            <span>Concepto de la línea</span>
            <span className="text-right">Debe (€)</span>
            <span className="text-right">Haber (€)</span>
            <span />
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-2 sm:grid-cols-[9rem_1fr_8rem_8rem_2rem] gap-2 items-start"
            >
              <AccountInput
                value={r.code}
                onChange={(code) => update(i, { code })}
                accounts={accounts}
                listId="ctb-accounts-list"
                className={`${inputCls} font-mono`}
              />
              <input
                value={r.concept}
                onChange={(e) => update(i, { concept: e.target.value })}
                placeholder="Opcional"
                maxLength={300}
                className={`${inputCls} col-span-2 sm:col-span-1`}
              />
              <input
                value={r.debit}
                onChange={(e) =>
                  update(i, { debit: e.target.value, credit: e.target.value ? '' : r.credit })
                }
                inputMode="decimal"
                placeholder="0,00"
                className={`${inputCls} font-mono text-right`}
                aria-label="Debe"
              />
              <input
                value={r.credit}
                onChange={(e) =>
                  update(i, { credit: e.target.value, debit: e.target.value ? '' : r.debit })
                }
                inputMode="decimal"
                placeholder="0,00"
                className={`${inputCls} font-mono text-right`}
                aria-label="Haber"
              />
              <button
                type="button"
                onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))}
                className="p-1.5 t-muted hover:text-rose-300"
                aria-label="Quitar línea"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, emptyRow()])}
            className="text-xs text-toga-300 hover:underline inline-flex items-center gap-1 mt-1"
          >
            <Plus size={12} /> Añadir línea
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-sm border-t border-base pt-3">
          <span className="t-muted">
            Debe <strong className="font-mono t-primary">{formatCents(totals.debit)}</strong>
          </span>
          <span className="t-muted">
            Haber <strong className="font-mono t-primary">{formatCents(totals.credit)}</strong>
          </span>
          <span
            className={
              totals.diff === 0 && totals.debit > 0 ? 'text-emerald-300' : 'text-amber-300'
            }
          >
            {totals.diff === 0 && totals.debit > 0 ? (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 size={14} /> Cuadra
              </span>
            ) : (
              <>
                Diferencia <strong className="font-mono">{formatCents(totals.diff)}</strong>
              </>
            )}
          </span>
        </div>

        {error && (
          <div
            role="alert"
            className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-start gap-2"
          >
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
          >
            {editing ? 'Guardar cambios' : 'Guardar borrador'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(true)}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {editing ? 'Guardar y contabilizar' : 'Contabilizar'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  )
}
